import { useEffect, useState, useSyncExternalStore } from "react";
import { formatDistanceToNow } from "date-fns";
import { CloudOff, RefreshCw } from "lucide-react";
import { getCachedAt, onStalenessChange } from "@/lib/staleness";
import { refreshData } from "@/lib/refresh";

// One banner for the whole app. The service worker serves the last good API
// response when the network is gone or too slow, which is the point — but a
// month's total from yesterday looks exactly like today's, so say so.
export function StaleBanner() {
  const cachedAt = useSyncExternalStore(
    onStalenessChange,
    getCachedAt,
    () => null,
  );
  const [, tick] = useState(0);

  // Re-render once a minute so "2 minutes ago" does not sit there lying.
  useEffect(() => {
    if (cachedAt === null) return;
    const id = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [cachedAt]);

  // Connectivity back: refetch, which clears this on its own.
  useEffect(() => {
    const onOnline = () => refreshData();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  if (cachedAt === null) return null;

  // 0 is the "cached, but the response carried no readable date" case.
  const age =
    cachedAt === 0
      ? "saved data"
      : `data from ${formatDistanceToNow(new Date(cachedAt), { addSuffix: true })}`;

  return (
    // Floats above the bottom nav, same language as it: fixed, inert wrapper,
    // only the pill itself takes taps.
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 lg:pl-64">
      <div className="mx-auto max-w-md px-4 pb-[calc(5rem+max(0.75rem,env(safe-area-inset-bottom)))] lg:max-w-6xl lg:px-10 lg:pb-4">
        <div className="pointer-events-auto mx-auto flex w-fit items-center gap-2 rounded-full bg-amber-100/90 py-1.5 pl-3 pr-1.5 text-xs font-medium text-amber-800 shadow-sm ring-1 ring-amber-200 backdrop-blur-xl">
          <CloudOff className="size-3.5 shrink-0" />
          <span>Showing {age}</span>
          <button
            type="button"
            onClick={() => refreshData()}
            className="flex items-center gap-1 rounded-full bg-amber-200/70 px-2 py-1 transition-colors active:bg-amber-300"
          >
            <RefreshCw className="size-3" />
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}
